// Slice 47 — Settings screen. Four cards (GDPR / Signing key / Staff /
// License server) over the GET /admin/settings rollup + GET /admin/staff
// list + POST /admin/settings/test-signing-proxy probe.

import { useEffect, useState } from "react";
import {
  type AuthPolicy,
  type ProxyTestResult,
  SettingsApi,
  type SettingsRollup,
  type SigningKeyEntry,
  StaffApi,
  type StaffRow,
} from "../../api/client";
import { useShell } from "../../shell/HqShellContext";
import {
  HqAvatar,
  HqBadge,
  HqButton,
  HqCard,
  HqInput,
  HqModal,
  HqStatusDot,
  HqToggle,
  Icons,
} from "../../ui";

export function SettingsScreen() {
  const { role, toast } = useShell();
  const isAdmin = role === "admin";
  const [rollup, setRollup] = useState<SettingsRollup | null>(null);
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [authPolicy, setAuthPolicyState] = useState<AuthPolicy | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [signingKeys, setSigningKeys] = useState<SigningKeyEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);
  const [probe, setProbe] = useState<ProxyTestResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const [s, st, ap, sk] = await Promise.all([
        SettingsApi.get(),
        StaffApi.list(),
        SettingsApi.getAuth(),
        SettingsApi.signingKeys(),
      ]);
      if (cancelled) return;
      setLoading(false);
      if (s.ok) setRollup(s.data);
      if (st.ok) setStaff(st.data);
      if (ap.ok) setAuthPolicyState(ap.data);
      if (sk.ok) setSigningKeys(sk.data);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function toggleRequirePasskey(next: boolean) {
    setAuthBusy(true);
    const res = await SettingsApi.setAuth(next);
    setAuthBusy(false);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    setAuthPolicyState(res.data);
    toast(
      next
        ? "Password sign-in disabled — staff must use a passkey."
        : "Password sign-in re-enabled.",
      next ? "warn" : "success",
    );
  }

  async function probeSigning() {
    setProbing(true);
    const res = await SettingsApi.testSigningProxy();
    setProbing(false);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    setProbe(res.data);
    if (res.data.ok) {
      toast(`Signing-proxy OK · ${res.data.latencyMs ?? "?"} ms`, "success");
    } else {
      toast(`Signing-proxy probe failed: ${res.data.error ?? "unknown"}`, "danger");
    }
  }

  if (!rollup) {
    return (
      <HqCard title="Settings" icon={Icons.Settings}>
        <p
          style={{
            margin: 0,
            color: "var(--hq-text-dim)",
            fontSize: 13,
            textAlign: "center",
            padding: "16px 0",
          }}
        >
          {loading ? "Loading…" : "Failed to load."}
        </p>
      </HqCard>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Header />
      <GdprCard rollup={rollup} />
      <SigningCard
        rollup={rollup}
        probing={probing}
        probe={probe}
        onProbe={() => void probeSigning()}
        signingKeys={signingKeys}
        canEdit={isAdmin}
        onAdded={(k) => setSigningKeys((prev) => [...prev, k])}
        onRetired={(id, retiredAt) =>
          setSigningKeys((prev) => prev.map((k) => (k.id === id ? { ...k, retiredAt } : k)))
        }
      />
      <StaffCard staff={staff} />
      <AuthPolicyCard
        policy={authPolicy}
        canEdit={isAdmin}
        busy={authBusy}
        onToggle={(v) => void toggleRequirePasskey(v)}
        staffWithoutPasskeys={staff.filter((s) => s.credentialCount === 0).length}
      />
      <LicenseServerCard rollup={rollup} />
    </div>
  );
}

function Header() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Icons.Settings width={20} height={20} sw={1.7} style={{ color: "var(--hq-accent)" }} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-.02em",
            color: "var(--hq-text)",
            lineHeight: 1.2,
          }}
        >
          Settings
        </h1>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          GDPR · signing-key · staff · license-server status
        </span>
      </div>
    </div>
  );
}

function GdprCard({ rollup }: { rollup: SettingsRollup }) {
  return (
    <HqCard title="Data processing · GDPR" icon={Icons.Shield}>
      <p
        style={{
          margin: "0 0 14px",
          color: "var(--hq-text-muted)",
          fontSize: 12.5,
          lineHeight: 1.55,
        }}
      >
        Data controller <strong>{rollup.gdpr.dataController}</strong> · Data protection officer{" "}
        <code className="hq-mono">{rollup.gdpr.dpoEmail}</code>.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <MetaCell label="Primary storage region" value={rollup.gdpr.primaryRegion} tone="success" />
        <MetaCell
          label="Open DSRs"
          value={rollup.gdpr.openDsrCount.toString()}
          sub="30-day clock"
          {...(rollup.gdpr.openDsrCount > 0 ? { tone: "warn" as const } : {})}
        />
        <MetaCell
          label="Active subprocessors"
          value={rollup.gdpr.activeSubprocessors.toString()}
          sub="Art. 28 register"
        />
        <MetaCell
          label="ROPA version"
          value={rollup.gdpr.ropaVersion}
          sub={`${rollup.gdpr.activeCustomers} active customers`}
          mono
        />
      </div>
    </HqCard>
  );
}

function SigningCard({
  rollup,
  probing,
  probe,
  onProbe,
  signingKeys,
  canEdit,
  onAdded,
  onRetired,
}: {
  rollup: SettingsRollup;
  probing: boolean;
  probe: ProxyTestResult | null;
  onProbe: () => void;
  signingKeys: SigningKeyEntry[];
  canEdit: boolean;
  onAdded: (k: SigningKeyEntry) => void;
  onRetired: (id: string, retiredAt: number) => void;
}) {
  const { toast } = useShell();
  const [registerOpen, setRegisterOpen] = useState(false);
  return (
    <HqCard
      title="Signing key"
      icon={Icons.Key}
      right={
        <HqStatusDot
          tone={rollup.signing.proxyHealthy ? "success" : "danger"}
          pulse={!rollup.signing.proxyHealthy}
          size={8}
        />
      }
    >
      <p
        style={{
          margin: "0 0 12px",
          color: "var(--hq-text-muted)",
          fontSize: 12.5,
          lineHeight: 1.55,
        }}
      >
        The Ed25519 private key lives <strong>only</strong> in the air-gapped signing-proxy (Slice
        38). The HQ never holds it — this card surfaces the public key the customer's Vyvern build
        verifies against.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <span
            className="hq-mono"
            style={{
              fontSize: 9.5,
              color: "var(--hq-text-dim)",
              letterSpacing: ".1em",
              textTransform: "uppercase",
            }}
          >
            Provider
          </span>
          <div
            className="hq-mono"
            style={{ fontSize: 11.5, color: "var(--hq-text)", marginTop: 4 }}
          >
            {rollup.signing.describe}
          </div>
        </div>
        <div>
          <span
            className="hq-mono"
            style={{
              fontSize: 9.5,
              color: "var(--hq-text-dim)",
              letterSpacing: ".1em",
              textTransform: "uppercase",
            }}
          >
            Trust set (multi-key, Slice 52)
          </span>
          {signingKeys.length === 0 ? (
            <pre
              className="hq-mono"
              style={{
                margin: "4px 0 0",
                padding: "8px 10px",
                background: "var(--hq-bg-input)",
                border: "1px solid var(--hq-border-input)",
                borderRadius: "var(--hq-radius-md)",
                fontSize: 10.5,
                color: rollup.signing.publicKeyHex ? "var(--hq-text)" : "var(--hq-text-dim)",
                wordBreak: "break-all",
                whiteSpace: "pre-wrap",
              }}
            >
              {rollup.signing.publicKeyHex ?? "(proxy unreachable — probe to retry)"}
            </pre>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
              {signingKeys.map((k) => (
                <SigningKeyRowView
                  key={k.id}
                  entry={k}
                  canEdit={canEdit}
                  onRetire={async () => {
                    if (
                      !confirm(
                        `Retire signing key "${k.id}"? New licenses will use a different key from now on; already-issued licenses keep verifying.`,
                      )
                    ) {
                      return;
                    }
                    const res = await SettingsApi.retireSigningKey(k.id);
                    if (!res.ok) {
                      toast(res.error.message, "danger");
                      return;
                    }
                    onRetired(k.id, res.data.retiredAt ?? Date.now());
                    toast(
                      res.data.alreadyRetired
                        ? `Key "${k.id}" was already retired.`
                        : `Key "${k.id}" retired.`,
                      "success",
                    );
                  }}
                />
              ))}
            </div>
          )}
        </div>
        {probe && (
          <div
            style={{
              padding: "8px 10px",
              borderRadius: "var(--hq-radius-md)",
              background: probe.ok ? "var(--hq-success-soft)" : "var(--hq-danger-soft)",
              border: `1px solid ${probe.ok ? "rgba(52,211,153,.25)" : "rgba(251,113,133,.25)"}`,
              color: probe.ok ? "var(--hq-success)" : "var(--hq-danger)",
              fontSize: 11.5,
              fontFamily: "var(--hq-font-mono)",
            }}
          >
            {probe.ok
              ? `Probe OK · ${probe.latencyMs ?? "?"} ms · ${probe.describe}`
              : `Probe failed: ${probe.error ?? "unknown"}`}
          </div>
        )}
        <div style={{ display: "flex", gap: 6 }}>
          <HqButton
            variant="secondary"
            leadingIcon={Icons.Sparkles}
            onClick={onProbe}
            loading={probing}
          >
            Test signing-proxy
          </HqButton>
          {canEdit && (
            <HqButton variant="ghost" onClick={() => setRegisterOpen(true)}>
              <Icons.Plus width={13} height={13} sw={2.2} />
              Register key
            </HqButton>
          )}
        </div>
      </div>
      <RegisterSigningKeyModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onAdded={(k) => {
          onAdded(k);
          setRegisterOpen(false);
        }}
      />
    </HqCard>
  );
}

function RegisterSigningKeyModal({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: (k: SigningKeyEntry) => void;
}) {
  const { toast } = useShell();
  const [id, setId] = useState("");
  const [hex, setHex] = useState("");
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const res = await SettingsApi.addSigningKey({
      id: id.trim(),
      publicKeyHex: hex.trim().toLowerCase(),
      ...(slot.trim() ? { proxySlot: slot.trim() } : {}),
    });
    setBusy(false);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    onAdded(res.data);
    setId("");
    setHex("");
    setSlot("");
    toast(`Signing key "${res.data.id}" registered.`, "success");
  };

  return (
    <HqModal open={open} onClose={onClose} title="Register signing key">
      <p style={{ margin: "0 0 14px", fontSize: 12.5, color: "var(--hq-text-muted)" }}>
        Generate the keypair on your air-gapped host (CLI), then register only the public half + the
        proxy slot here. The private half stays with the signing-proxy via
        <code className="hq-mono"> LS_SIGNING_KEY_HEX_&lt;slot&gt;</code>.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <FieldLabel label="Key id (e.g. 2026b-prod)">
          <HqInput
            placeholder="2026b-prod"
            value={id}
            onChange={(e) => setId(e.target.value)}
            mono
            autoFocus
          />
        </FieldLabel>
        <FieldLabel label="Public key (64 lowercase hex chars)">
          <HqInput
            placeholder="ed25519 pubkey hex"
            value={hex}
            onChange={(e) => setHex(e.target.value)}
            mono
          />
        </FieldLabel>
        <FieldLabel label="Proxy slot (optional, e.g. 1)">
          <HqInput placeholder="1" value={slot} onChange={(e) => setSlot(e.target.value)} mono />
        </FieldLabel>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <HqButton variant="secondary" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton onClick={() => void submit()} disabled={busy || !id.trim() || !hex.trim()}>
            {busy ? "Registering…" : "Register key"}
          </HqButton>
        </div>
      </div>
    </HqModal>
  );
}

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: "var(--hq-text-dim)" }}>{label}</span>
      {children}
    </label>
  );
}

function SigningKeyRowView({
  entry,
  canEdit,
  onRetire,
}: {
  entry: SigningKeyEntry;
  canEdit: boolean;
  onRetire: () => Promise<void>;
}) {
  const retired = entry.retiredAt != null;
  return (
    <div
      style={{
        padding: 10,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="hq-mono" style={{ fontSize: 12, color: "var(--hq-text)" }}>
          {entry.id}
        </span>
        <HqBadge tone={retired ? "neutral" : "success"} size="sm">
          {retired ? "retired" : "active"}
        </HqBadge>
        {entry.proxySlot && (
          <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
            slot {entry.proxySlot}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {canEdit && !retired && (
          <HqButton variant="ghost" size="sm" onClick={() => void onRetire()}>
            Retire
          </HqButton>
        )}
        <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
          added {fmtDate(entry.addedAt)}
          {retired && entry.retiredAt && ` · retired ${fmtDate(entry.retiredAt)}`}
        </span>
      </div>
      <code
        className="hq-mono"
        style={{
          fontSize: 10,
          color: "var(--hq-text-muted)",
          wordBreak: "break-all",
          opacity: retired ? 0.6 : 1,
        }}
      >
        {entry.publicKeyHex}
      </code>
    </div>
  );
}

function StaffCard({ staff }: { staff: StaffRow[] }) {
  return (
    <HqCard
      title="Staff access"
      icon={Icons.Users}
      right={
        <HqButton variant="ghost" size="sm" disabled title="Lands in V2 (passkey-bound enrollment)">
          Invite staff
        </HqButton>
      }
    >
      <p
        style={{ margin: "0 0 12px", color: "var(--hq-text-muted)", fontSize: 12, lineHeight: 1.5 }}
      >
        V1 is single-superadmin by design. Slice 47 forward-declares the invite flow; passkey-bound
        enrollment lands in V2.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {staff.map((s) => (
          <div
            key={s.id}
            style={{
              padding: 10,
              background: "var(--hq-bg-input)",
              border: "1px solid var(--hq-border-input)",
              borderRadius: "var(--hq-radius-md)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <HqAvatar name={s.username} size={32} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--hq-text)" }}>
                {s.username}
              </div>
              <div className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
                created {fmtDate(s.createdAt)}
                {s.lastLoginAt && ` · last login ${fmtDate(s.lastLoginAt)}`}
              </div>
            </div>
            <HqBadge tone={s.role === "admin" ? "accent" : "neutral"} size="sm">
              {s.role}
            </HqBadge>
          </div>
        ))}
      </div>
    </HqCard>
  );
}

function AuthPolicyCard({
  policy,
  canEdit,
  busy,
  onToggle,
  staffWithoutPasskeys,
}: {
  policy: AuthPolicy | null;
  canEdit: boolean;
  busy: boolean;
  onToggle: (next: boolean) => void;
  staffWithoutPasskeys: number;
}) {
  const requirePasskey = policy?.requirePasskey ?? false;
  const wouldLockOut = requirePasskey === false && staffWithoutPasskeys > 0;
  return (
    <HqCard title="Authentication policy" icon={Icons.Lock}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: "4px 0 14px",
          borderBottom: "1px solid var(--hq-border)",
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: "var(--hq-text)", fontWeight: 500 }}>
            Require passkey for sign-in
          </div>
          <div style={{ fontSize: 11.5, color: "var(--hq-text-dim)", marginTop: 2 }}>
            When on, /admin/login refuses password sign-in. Staff with no passkey are locked out
            until they claim an invite or add one.
          </div>
        </div>
        <HqToggle
          on={requirePasskey}
          onChange={onToggle}
          disabled={!canEdit || busy}
          ariaLabel="Require passkey"
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
        <MetaCell
          label="State"
          value={requirePasskey ? "passkey-only" : "password OR passkey"}
          tone={requirePasskey ? "warn" : "success"}
          mono
        />
        <MetaCell
          label="Staff without a passkey"
          value={String(staffWithoutPasskeys)}
          {...(wouldLockOut && requirePasskey ? { tone: "danger" as const } : {})}
          mono
        />
      </div>
      {policy && policy.updatedAt > 0 && (
        <p
          style={{
            margin: "12px 0 0",
            fontSize: 11,
            color: "var(--hq-text-dim)",
            fontFamily: "var(--hq-font-mono)",
          }}
        >
          Last changed {fmtDate(policy.updatedAt)} by {policy.updatedBy}.
        </p>
      )}
      {!canEdit && (
        <p style={{ margin: "8px 0 0", fontSize: 11, color: "var(--hq-text-dim)" }}>
          Read-only: only admins can change this policy.
        </p>
      )}
    </HqCard>
  );
}

function LicenseServerCard({ rollup }: { rollup: SettingsRollup }) {
  return (
    <HqCard title="License server" icon={Icons.Activity}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <MetaCell label="Version" value={rollup.licenseServer.version} mono />
        <MetaCell
          label="Mode"
          value={rollup.licenseServer.mode}
          tone={rollup.licenseServer.mode === "air-gapped" ? "success" : "warn"}
          mono
        />
      </div>
      <p
        style={{
          margin: "14px 0 0",
          fontSize: 11.5,
          color: "var(--hq-text-dim)",
          lineHeight: 1.55,
        }}
      >
        <strong>Air-gapped:</strong> the web layer (this server) is <em>not</em> the same host that
        holds the signing key. The proxy runs on a separate Dokploy app; the web layer just calls{" "}
        <code className="hq-mono">/sign</code> via a shared secret. <strong>Single-host</strong>{" "}
        means key + web are on the same box — explicit escape hatch, never silent (set
        <code className="hq-mono">LS_SIGNING_MODE=local</code>).
      </p>
    </HqCard>
  );
}

function MetaCell({
  label,
  value,
  sub,
  mono,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  mono?: boolean;
  tone?: "success" | "warn" | "danger";
}) {
  const color =
    tone === "danger"
      ? "var(--hq-danger)"
      : tone === "warn"
        ? "var(--hq-warn)"
        : tone === "success"
          ? "var(--hq-success)"
          : "var(--hq-text)";
  return (
    <div
      style={{
        padding: 12,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 9.5,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          fontFamily: mono ? "var(--hq-font-mono)" : "var(--hq-font-sans)",
          color,
        }}
      >
        {value}
      </span>
      {sub && <span style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>{sub}</span>}
    </div>
  );
}

function fmtDate(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
